/**
 * カード — 内政（ないせい）20枚
 *
 * docs/01-ゲームデザイン.md §9 のカード一覧のうち、内政札を担当する。
 * 効果は core の Effect 配列で表現し、core 側に if 文を足さないで済むようにする。
 *
 * 【この20枚の設計意図】
 * 内政は「合戦の準備」ではない。戦国の百年は、検地・治水・用水・市・街道といった
 * 地味な仕事の積み重ねでもあった。したがってこの20枚は、
 *   ・銭と石高を生む（楽市楽座・六斎市・鉱山・新田・湊・特産）
 *   ・人と物の動きを変える（街道と伝馬・関所停止）
 *   ・国の仕組みを整える（検地・分国法・撰銭令・徳政令・城下町）
 *   ・村と町の側の力を盤上に出す（惣掟・寺内町）
 *   ・合戦のルールに触れる（鉄砲鍛冶・兵糧の備え・石垣普請）
 * という配分にしてある。合戦で使う札は docs/06 のホールデムに噛み合う形
 * （formationMod / holeCardMod）だけを使い、出目修正は盤上の進軍にのみ用いる。
 *
 * 執筆基準は provinces/_reference.ts と docs/05-学習設計.md §4 に従う。
 * とくに §4-4（女性・民衆の存在を消さない）は、内政札の担当範囲そのものである。
 */

import type { Card } from '../types';

// ---------------------------------------------------------------------------
// 市と銭
// ---------------------------------------------------------------------------

/** 市場の特権を解いて人と物を呼ぶ。信長の独創ではない点を caveat に必ず書く。 */
export const RAKUICHI_RAKUZA: Card = {
  id: 'card:naisei:rakuichi',
  name: '楽市楽座',
  reading: 'らくいちらくざ',
  type: 'naisei',
  cost: { zeni: 2 },
  timing: 'council',
  // 文献上の最古の楽市令は天文18年(1549)。第II章（1521-1559）以降に解禁する
  availableFrom: 2,
  effects: [
    // 座の特権と市場の税を免じ、商人を呼び込む
    { t: 'gain', res: 'zeni', n: 3 },
    { t: 'gain', res: 'koku', n: 1 },
  ],
  note: {
    summary:
      '城下の市で座の特権や市場税を免じ、誰でも自由に商いができるようにした法令。織田信長の政策として知られるが、先行する例がいくつも確認されている。',
    detail: {
      1: '町の市で、決まった商人だけが売れるというしくみ（座）をやめ、だれでも自由に店を出せるようにしたきまりです。市ににぎわいがもどり、町に人やお金が集まりました。織田信長がおこなったことで知られています。',
      2: '戦国大名が城下町の市に出した法令で、座の特権や市場での税・課役を免除し、新しく来た商人でも自由に商売できるようにしたもの。市の賑わいを生み、城下に人と物を集める狙いがあった。織田信長が永禄10年（1567年）に美濃の加納に出したもの、天正5年（1577年）に安土山下町に出したものがよく知られるが、天文18年（1549年）に近江の六角定頼が石寺新市に出した例など、信長より前の例も確認されている。',
      3: '城下町や新設の市に対して出された経済法令で、既存の座が持つ独占的な販売権、市場での諸税・諸役、他国商人への規制などを免除し、商人の参入を促した。文献上で確認できる最も古い楽市令は天文18年(1549)に近江の六角定頼が観音寺城下の石寺新市に出したもので、永禄9年(1566)には今川氏真が駿河の富士大宮の市に出している。織田信長のものは永禄10年(1567)の美濃加納、元亀3年(1572)の近江金森、天正5年(1577)の安土山下町中掟書が知られる。安土の掟書では、楽市とすること、往来する商人に安土での宿泊を義務づけること、火事・喧嘩の際の扱いなどが定められており、単なる自由化ではなく、城下へ流通を集めるための統制でもあった。市に立つ商人には女性も多く、炭や薪を売る大原女、鮎などを売る桂女のように、行商で生計を立てた人々の姿が記録や絵画に残っている。近年の研究では、楽市楽座を「信長の革新的な自由経済政策」とする理解は見直され、先行例の存在と、座がその後も存続した地域が多いことが指摘されている。',
    },
    year: [1549, 1590],
    keywords: ['楽市楽座', '座', '城下町', '六角定頼', '織田信長', '市'],
    caveat:
      '楽市楽座は織田信長が考え出したものではありません。文献上の最古の例は天文18年(1549)の六角定頼によるもので、今川氏真の例もあります。また信長が全国の座を廃止したわけではなく、京都や奈良などでは座がその後も続きました。',
    gameNote:
      '盤上では「銭+3・石高+1」という一度きりの収入にしていますが、実際の楽市令は特定の市や町に対して出された地域的な法令で、領国全体の収入が即座に増えるものではありません。この札は文献上の最古の例（1549年）に合わせて第II章以降にしか出てきません。',
  },
};

/** 月6回の定期市。行商人と女性商人の姿を盤上に出すための札。 */
export const ROKUSAI_ICHI: Card = {
  id: 'card:naisei:rokusai',
  name: '六斎市',
  reading: 'ろくさいいち',
  type: 'naisei',
  cost: { zeni: 1 },
  timing: 'council',
  effects: [
    // 市が月6回立ち、人と物と知らせが集まる
    { t: 'gain', res: 'zeni', n: 2 },
    { t: 'draw', n: 1 },
  ],
  note: {
    summary:
      '月に6回開かれた定期市。鎌倉期の月3回（三斎市）から回数が増えたもので、行商人が市から市へと巡り、村の百姓も売り買いに加わった。',
    detail: {
      1: 'ひと月に6回ひらかれる市のことです。むかしは月に3回でしたが、売り買いがさかんになって回数がふえました。商人は市から市へと歩いてまわり、村の人たちも作ったものを売りにきました。',
      2: '月に6回開かれる定期市。鎌倉時代には月3回の三斎市が一般的だったが、室町時代に商品の流通が増えるにつれて六斎市が広がった。市は寺社の門前や街道の辻、湊などに立ち、連雀商人（背負い道具で品を運ぶ行商人）や振売（天秤棒で担いで売り歩く人）が市から市へと巡った。行商には女性も多く、京の周辺では炭や薪を売る大原女、鮎などを売る桂女が知られる。市では銭が用いられ、村の百姓も余った作物や布を売って銭を得た。',
      3: '毎月決まった6日に開かれる定期市で、鎌倉期に一般化した三斎市（月3回）から発展したもの。応仁の乱前後には各地に広がり、寺社の門前、街道の辻、湊、城下などに立った。市には連雀商人・振売といった行商人が巡回し、彼らは複数の市の開催日をつないで回った。行商には女性の担い手が多く、洛北から炭・薪を運んだ大原女、桂川の漁労集団に出自を持つ桂女などが史料や洛中洛外図屏風に描かれている。市場には市場税や市場銭が課され、その徴収権をめぐって寺社・公家・大名が争ったため、楽市令のような免除の法令が意味を持った。また市には「市場の平和」と呼ばれる特殊な秩序があり、市庭では喧嘩や債務の取り立てが制限されるという慣行が各地で確認される。市が定期的に立つこと自体が、村の百姓が銭を手にする回路であり、年貢の銭納が広がる前提でもあった。',
    },
    year: [1400, 1600],
    keywords: ['六斎市', '定期市', '連雀商人', '振売', '大原女', '桂女'],
    gameNote:
      '盤上では「銭+2・カードを1枚引く」としています。市に人と知らせが集まることを札の補充で表した処理で、実際の市が情報網として機能した度合いを数値化したものではありません。',
  },
};

/** 悪銭をめぐる混乱への対処。貨幣の話を盤上に出す。 */
export const ERIZENI_REI: Card = {
  id: 'card:naisei:erizeni',
  name: '撰銭令',
  reading: 'えりぜにれい',
  type: 'naisei',
  cost: { gunryaku: 1 },
  timing: 'council',
  effects: [
    // 銭の使い方に基準を定め、領内の取引を落ち着かせる
    { t: 'gain', res: 'zeni', n: 2 },
    { t: 'gain', res: 'gunryaku', n: 1 },
  ],
  note: {
    summary:
      '質の悪い銭の受け取りを拒む「撰銭」が取引を滞らせたため、幕府や大名が銭の混ぜ方や交換比率を定めた法令。',
    detail: {
      1: 'そのころのお金は中国から来た銅銭で、こわれたものや作りのわるいものがまじっていました。人びとはよい銭だけを選んで受け取ろうとし（撰銭）、売り買いがうまくいかなくなったので、銭の使い方のきまりが出されました。',
      2: '中世の日本では中国から輸入された銅銭が使われていたが、粗悪な模鋳銭や欠けた銭（鐚銭）が混じるようになり、人々が良い銭だけを選んで受け取ろうとする撰銭が広がった。これが取引の妨げになったため、幕府や大名は撰銭令を出し、悪銭をどの割合まで混ぜてよいか、悪銭を良銭の何枚分と数えるかなどを定めた。大名では文明17年（1485年）の大内氏の例が古く、室町幕府は明応9年（1500年）以降くり返し撰銭令を出している。織田信長も永禄12年（1569年）に京都で銭を三段階に分け、金銀での支払いも認める法を出した。',
      3: '中世日本は自前の貨幣を鋳造せず、宋銭・明銭など中国銭を輸入して基準通貨とした。取引の拡大に対して銭の絶対量が不足し、私鋳銭や模鋳銭、破損して欠けた鐚銭（びたせん）が流通に混じると、受取側が良質な銭を選別して受け取る撰銭が横行し、決済が滞った。これに対し発行されたのが撰銭令で、内容は「撰銭の禁止」一辺倒ではなく、悪銭の混入比率の指定、悪銭と精銭の換算率の設定、特定の銭種の受取強制など多様である。大名の例では文明17年(1485)の大内氏が古く、室町幕府は明応9年(1500)以降しばしば発令した。永禄12年(1569)の織田信長の法は、京都で流通する銭を品質により三段階に分けて交換比率を定め、金銀を高額決済に用いることを認めた点に特徴がある。撰銭令の効果は限定的で、16世紀後半には米や金銀が銭に代わる支払手段として比重を増していく。かつては「永楽通宝が全国の基準銭だった」と説明されたが、近年の研究では地域ごとに基準となる銭種が異なっていたことが明らかにされている。',
    },
    year: [1485, 1569],
    keywords: ['撰銭令', '鐚銭', '永楽通宝', '大内氏', '織田信長', '貨幣'],
    caveat:
      '「永楽通宝が戦国時代の全国共通の基準銭だった」という説明は近年見直されており、東国と西国で基準となる銭種や換算のしかたが違っていたことがわかっています。撰銭令がどれほど守られたかについても史料的な確認は難しいとされています。',
    gameNote:
      '盤上では「銭+2・軍略+1」としていますが、撰銭令は収入を増やす法ではなく、取引の混乱を抑えるための法です。取引が落ち着くことの利を、遊びの上でこの数値に置き換えています。',
  },
};

/** 借金の帳消し。土一揆と徳政の関係を二択にする。 */
export const TOKUSEI_REI: Card = {
  id: 'card:naisei:tokusei',
  name: '徳政令',
  reading: 'とくせいれい',
  type: 'naisei',
  cost: { gunryaku: 2 },
  timing: 'council',
  effects: [
    {
      t: 'choice',
      options: [
        {
          // 徳政を認める。土倉・酒屋からの収入は減るが、村と町の負担は軽くなる
          label: '徳政を認め、借銭を破棄させる',
          then: [
            { t: 'lose', res: 'zeni', n: 2 },
            { t: 'gain', res: 'ishin', n: 2 },
            { t: 'gain', res: 'koku', n: 1 },
          ],
        },
        {
          // 認めず、貸し手を保護する。分一銭を取る道もあった
          label: '認めず、土倉を保護して分一銭を取る',
          then: [
            { t: 'gain', res: 'zeni', n: 2 },
            { t: 'lose', res: 'ishin', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '借金を破棄させ、質入れした土地を元の持ち主に戻させる法令。土一揆が徳政を求めて蜂起することも多く、幕府は手数料（分一銭）を取って徳政を認めることもあった。',
    detail: {
      1: '借りたお金を返さなくてよい、質に入れた田畑を返してもらえる、というきまりです。くらしに困った人びとが集まって「徳政を出してほしい」とうったえることもありました。',
      2: '債務の破棄と、質入れ・売却された土地の返還を認める法令。正長元年（1428年）の正長の土一揆では近江の馬借の蜂起をきっかけに各地で徳政が求められ、嘉吉元年（1441年）の嘉吉の徳政一揆を受けて幕府は初めて全国的な徳政令を出した。以後、幕府は債務額の一部（分一銭）を納めさせたうえで徳政を認める分一徳政令を出し、財源としても用いた。戦国大名も領国内で徳政を出しており、家臣や百姓の困窮に対処する手段のひとつだった。',
      3: '債務の破棄および売却・質入れされた所領の無償返還を命じる法令。鎌倉幕府の永仁の徳政令（1297）に淵源を持つが、室町期には土一揆と結びついて頻発した。正長元年(1428)、近江坂本の馬借の蜂起に始まる正長の土一揆では、京都周辺の土倉・酒屋が襲われ、大和では興福寺が私徳政を認めた記録が柳生の徳政碑文として残る。嘉吉元年(1441)の嘉吉の徳政一揆では幕府が初めて全国を対象とする徳政令を発した。以後、幕府は債務額の1割ないし2割を分一銭として納入させる分一徳政令、逆に債権者から分一銭を取って債権を保護する分一徳政禁制を併用し、財政収入の一手段とした。戦国大名の分国法にも徳政の規定が見え、家臣の所領の質入れ回復や、飢饉時の百姓の債務処理として運用された。徳政を求める側には百姓・馬借・地侍が含まれ、彼らは村や座のまとまりを背景に行動している。徳政は借り手を救う一方で貸し手である土倉・寺院の経営を圧迫し、金融の萎縮を招いた面もあり、どちらか一方の立場から評価できる制度ではない。',
    },
    year: [1428, 1590],
    keywords: ['徳政令', '土一揆', '土倉', '分一銭', '正長の土一揆', '嘉吉の徳政'],
    caveat:
      '徳政令は「民衆を救った良い法」とも「経済を混乱させた悪法」とも言い切れません。借り手の負担は軽くなりますが、貸し手である土倉や寺院の経営を圧迫し、その後の融資が受けにくくなるという影響もありました。',
    gameNote:
      '盤上では「認める／認めない」の二択にしていますが、実際の徳政は大名が一存で決められるものばかりではなく、一揆の要求や幕府の方針に押されて出される場合が多くありました。',
  },
};

// ---------------------------------------------------------------------------
// 土地と水
// ---------------------------------------------------------------------------

/** 土地を測り、年貢の基準を定める。増徴への反発も効果に含める。 */
export const KENCHI: Card = {
  id: 'card:naisei:kenchi',
  name: '検地',
  reading: 'けんち',
  type: 'naisei',
  cost: { gunryaku: 1 },
  timing: 'council',
  effects: [
    // 田畑の広さと収量が把握され、年貢と軍役の基準が定まる
    { t: 'gain', res: 'koku', n: 2 },
    // 負担が増える側の反発も同時に生じる
    { t: 'lose', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '田畑の広さと収穫量を調べ、年貢と軍役の基準を定める調査。家臣の自己申告による指出検地から、役人が実際に測る太閤検地へと変わった。',
    detail: {
      1: '田や畑の広さをはかり、どれくらい米がとれるかを調べることです。これをもとに、村がおさめる年貢の量や、武士が出す兵の数が決められました。調べられる側にとっては、負担がふえることもありました。',
      2: '領内の田畑の面積と収穫高を調査し、年貢と軍役の基準を定める政策。戦国大名は家臣や村に申告させる指出（さしだし）検地を行うことが多かったが、豊臣秀吉は天正10年（1582年）以降、役人を派遣して実際に土地を測る検地を進めた（太閤検地）。長さをはかる竿（六尺三寸を一間とする間竿）と、量をはかる枡（京枡）の基準をそろえ、収穫高を石高で表し、一つの土地に一人の耕作者を登録する一地一作人の原則をとった。これにより荘園以来の重なり合った権利が整理され、村と大名が直接向き合う形になった。',
      3: '田畑の面積・等級・収穫高を調査し、年貢賦課と軍役賦課の基準を確定する土地調査。戦国大名の段階では、家臣や村からの自己申告に基づく指出検地が中心で、今川・北条・武田などの例が知られる。豊臣政権は天正10年(1582)の山城を皮切りに全国で検地を進め、間竿と京枡を統一基準として面積と容積を規格化し、上・中・下の等級ごとに石盛を定めて石高を算出した。検地帳には土地ごとに名請人を記載し、一地一作人の原則によって、それまで荘園領主・在地領主・作人が重層的に持っていた権利を整理した。これにより石高制が成立し、大名の知行も軍役も石高で表されるようになる。検地は年貢の増徴を伴うことが多く、各地で抵抗が起きた。天正18年(1590)から19年にかけての奥州の一揆（葛西大崎一揆・九戸政実の乱など）には、検地への反発が背景にあったとされる。村では女性も農作業と年貢の負担を担っており、検地は村の暮らし全体に及ぶ調査であった。太閤検地を「兵農分離を完成させた画期」と一括りにする理解は近年見直されており、地域ごとに実施の時期・方法・徹底度に大きな差があったことが指摘されている。',
    },
    year: [1500, 1598],
    keywords: ['検地', '太閤検地', '指出検地', '石高', '一地一作人', '京枡'],
    caveat:
      '太閤検地を「全国一律に、同じ方法で行われた」と説明するのは正確ではありません。実施の時期や方法は地域によって差があり、従来の慣行が残った地域もあります。検地帳の記載から当時の村の姿をどこまで復元できるかについても議論があります。',
    gameNote:
      '盤上では「石高+2・威信-1」としています。威信が下がるのは、年貢の増徴に対する村側の反発を表したもので、検地そのものが悪い政策だったという評価ではありません。',
  },
};

/** 川を制する。信玄堤に触れつつ、江戸期の改修という caveat を落とさない。 */
export const TSUTSUMI_BUSHIN: Card = {
  id: 'card:naisei:teibushin',
  name: '堤普請',
  reading: 'つつみぶしん',
  type: 'naisei',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    // 村から人足を出して堤を築く
    { t: 'lose', res: 'hei', n: 2 },
    { t: 'gain', res: 'koku', n: 2 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '川の氾濫を防ぐために堤を築く工事。人足として村から多くの人手を出させる大事業で、成功すれば新しい田が開け、失敗すれば村ごと流された。',
    detail: {
      1: '川があふれて田や家がながされないように、堤（つつみ）をきずく工事です。村から多くの人が人足として出て、何年もかけて行われました。武田信玄が甲斐でおこなった堤の工事がよく知られています。',
      2: '河川の氾濫を防ぐための堤防工事。戦国大名にとって治水は年貢の安定に直結する重要な事業で、村からは人足（普請役）として人手が徴発された。甲斐では釜無川と御勅使川の合流点に堤が築かれ、後に信玄堤と呼ばれた。堤を一直線に閉じるのではなく、途中に開口部を設けて水の勢いを逃がす霞堤（かすみてい）の考え方も用いられている。工事によって水害が減れば新田が開け、村の暮らしは安定したが、普請役の負担そのものは村にとって重いものだった。',
      3: '河川堤防の構築と維持を目的とする土木事業。戦国大名の領国経営において、治水は年貢収取の安定に直結し、開発可能な耕地の拡大にもつながるため重視された。甲斐の釜無川・御勅使川合流点の堤防群は「信玄堤」の名で知られ、御勅使川の流路を将棋頭と呼ばれる石積みで分け、竜王高岩に水勢を当てて勢いを殺し、雁行状に配した堤で受けるという構成をとる。ただし現在残る構造の大半は江戸時代以降の改修によるもので、武田氏当時の姿をそのまま伝えるものではない。堤の維持には毎年の補修が必要で、大名は近隣の村に堤を管理させる代わりに諸役を免除するなどの措置をとった（甲斐では堤に接して竜王河原宿を置いた例が知られる）。工事の労働力は村からの普請役として徴発され、動員は数千人規模に及ぶこともあった。治水事業を大名個人の英断として語ることは容易だが、実際には村々の労働と、代々にわたる補修の蓄積の上に成り立っている。',
    },
    year: [1520, 1600],
    keywords: ['治水', '信玄堤', '霞堤', '普請役', '釜無川', '人足'],
    caveat:
      '「信玄堤」として現存する堤防の多くは江戸時代以降の改修によるもので、武田信玄の時代の構造をそのまま残しているわけではありません。信玄が単独で設計したという理解も、後世の記録に基づく部分が大きいとされています。',
    gameNote:
      '盤上では「兵-2」を人足の徴発として表しています。実際に堤普請に出たのは兵ではなく村の百姓で、女性が炊き出しや資材運びに加わることもありました。兵という資源で代表させているのは、遊びの上の簡略化です。',
  },
};

/** 用水と溜池。村の共同管理と水争いを含める。 */
export const YOUSUI: Card = {
  id: 'card:naisei:yousui',
  name: '用水と溜池',
  reading: 'ようすいとためいけ',
  type: 'naisei',
  cost: { zeni: 1 },
  timing: 'council',
  effects: [
    { t: 'lose', res: 'hei', n: 1 },
    { t: 'gain', res: 'koku', n: 1 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '田に水を引くための井堰・水路・溜池をつくり、村が共同で維持した。水の分け方は村どうしの争い（水論）の原因にもなった。',
    detail: {
      1: '田に水を引くために、川をせき止めたり（井堰）、水路をほったり、池をつくったりしました。水路の手入れは村のみんなでおこないます。水の分け方をめぐって、村どうしがあらそうこともありました。',
      2: '稲作に必要な水を確保するための施設と、その維持のしくみ。川に井堰を設けて水路（用水）に水を引き、雨の少ない地域では溜池を築いた。用水路の掃除や堰の修理は村の共同作業として行われ、惣村では水の分配のしかたや、順番、水番の決め方を掟に定めた。水は限られた資源だったため、上流と下流の村の間で水論（水争い）が起こることも多く、大名や寺社が裁定に入ることもあった。',
      3: '灌漑用の井堰・用水路・溜池と、それを支える村落の管理体制。中世後期には惣村の発達に伴い、用水の維持管理が村の共同事業として制度化された。用水の浚渫や堰の修復は村人が出役して行い、費用や労力の分担、通水の順番、旱魃時の分水比率などが村掟に明記された例が近江の菅浦や今堀などの文書に残る。水は再生産の根幹であるだけに紛争の種でもあり、上下流の村落間、あるいは荘郷の境界をまたぐ形で水論が頻発した。解決は当事者間の相論、寺社や領主による裁定、時に武力を伴う実力行使まで幅があり、大名の分国法にも用水の相論に関する条文が置かれることがあった。溜池灌漑は讃岐・河内・大和など降水量の少ない地域で発達し、江戸時代の新田開発で大規模化する。用水の日常的な維持を担ったのは村の百姓であり、女性も田植えや水口の管理など水にかかわる労働に従事していた。灌漑の歴史は大名の事業史であるより前に、村の共同労働の歴史である。',
    },
    year: [1400, 1600],
    keywords: ['用水', '溜池', '井堰', '水論', '惣村', '灌漑'],
    gameNote:
      '盤上では「兵-1・石高+1・威信+1」という小さな札にしていますが、実際の用水管理は一度きりの事業ではなく、毎年くり返される共同作業でした。',
  },
};

/** 新田開発。大規模化は江戸期という caveat が要る。 */
export const SHINDEN_KAIHATSU: Card = {
  id: 'card:naisei:shinden',
  name: '新田開発',
  reading: 'しんでんかいはつ',
  type: 'naisei',
  cost: { zeni: 1 },
  timing: 'council',
  effects: [
    { t: 'lose', res: 'hei', n: 1 },
    { t: 'gain', res: 'koku', n: 2 },
  ],
  note: {
    summary:
      '原野や河原、湿地を切り開いて新しく田をつくること。戦国期には治水と一体で進められ、大規模な開発は江戸時代前期に本格化した。',
    detail: {
      1: '野原や川原、しめった土地を切りひらいて、新しく田をつくることです。堤をきずいて水をふせぐ工事とセットで行われました。田がふえれば米もふえ、国の力がつよくなります。',
      2: '原野・河原・湿地・干潟などを開墾して新しい田をつくること。戦国大名は治水工事と一体で開発を進め、開発した者に一定期間年貢を免除するなどの手立てで人を集めた。開発の担い手には土豪や有力百姓が多く、開発した土地の権利を持つ者を新田主と呼ぶこともあった。ただし大規模な新田開発が全国的に進むのは戦国期ではなく、社会が安定した江戸時代前期のことである。',
      3: '未墾地の開墾による耕地の拡大。中世を通じて漸進的に進んでいたが、戦国期には大名による河川改修と結びつき、氾濫原や後背湿地の開発が進んだ。大名は開発を促すために鍬下年季（開発後の一定期間、年貢を免除または軽減する措置）を与え、土豪・有力百姓・浪人などを開発の担い手として引き入れた。開発された土地の権利関係は複雑で、検地によって名請人が確定されるまで、開発者・領主・村の間で重層的に保持されることが多かった。全国の耕地面積は16世紀末におよそ150万町歩、18世紀初頭に約300万町歩と推計されており、耕地の倍増は主として江戸時代前期の新田開発によるものである。この時期には藩による開発のほか、町人請負新田の形で商人資本が投じられる例も現れる。したがって戦国期の新田開発は、江戸期の大規模開発の前段階として位置づけられる。開墾の労働は家族単位で行われ、女性も開墾・田植え・収穫の各段階に従事した。',
    },
    year: [1500, 1700],
    keywords: ['新田開発', '開墾', '鍬下年季', '治水', '耕地面積', '石高'],
    caveat:
      '新田開発が全国規模で進み、耕地面積が大きく増えたのは江戸時代前期です。戦国時代の開発は治水と結びついた局地的なもので、盤上のように短期間で石高が跳ね上がるものではありませんでした。',
    gameNote:
      '盤上では1枚のカードで石高が+2されますが、実際の開発には数年から数十年を要しました。「兵-1」は開墾に出す人手を表したもので、実際に働いたのは兵ではなく百姓です。',
  },
};

// ---------------------------------------------------------------------------
// 鉱山・湊・特産
// ---------------------------------------------------------------------------

/** 銀山・金山。灰吹法と、坑内労働の過酷さの両方を書く。 */
export const KOUZAN_KAIHATSU: Card = {
  id: 'card:naisei:kouzan',
  name: '鉱山開発',
  reading: 'こうざんかいはつ',
  type: 'naisei',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    // 間歩を掘り、精錬の職人を招く
    { t: 'lose', res: 'hei', n: 1 },
    { t: 'gain', res: 'zeni', n: 4 },
  ],
  note: {
    summary:
      '銀山・金山を開き、灰吹法などの精錬技術で産出を増やすこと。石見銀山の銀は大陸との交易に用いられ、大名の重要な財源となった。',
    detail: {
      1: '山をほって銀や金を取り出すことです。石見（いまの島根県）の銀山では、灰吹法という新しいやりかたで銀を取り出せるようになり、たくさんの銀がとれました。銀は外国との商売にも使われました。',
      2: '銀山・金山を開発し、産出量を増やす事業。石見銀山は大永6年（1526年）に博多の商人神屋寿禎が本格的な開発に着手したと伝えられ、天文2年（1533年）には博多から技術者を招いて灰吹法（鉛を用いて銀を取り出す精錬法）を導入した。これにより産出が飛躍的に増え、銀は大陸との交易の決済に用いられた。銀山をめぐっては大内氏・尼子氏・毛利氏が争い、支配者がたびたび入れ替わっている。但馬の生野銀山、甲斐の黒川金山なども知られる。',
      3: '鉱脈の探査、間歩（まぶ＝坑道）の掘削、精錬技術の導入からなる事業。石見銀山では大永6年(1526)に博多商人神屋寿禎が開発に着手したとされ、天文2年(1533)に博多から宗丹・桂寿を招いて灰吹法を導入した。灰吹法は鉛と合金にした銀を灰の上で加熱し、鉛を灰に吸収させて銀を残す方法で、これにより低品位鉱からも銀を得られるようになり産出が急増する。石見銀山は大内氏・尼子氏・毛利氏の争奪の的となり、のちに豊臣政権、江戸幕府の直轄となった。但馬生野銀山、佐渡金山、甲斐黒川金山、伊豆金山なども開発されている。鉱山では山師が採掘を請け負い、掘子・柄山負・鍛冶・炭焼など多様な職種が集まって鉱山町を形成した。坑内は粉塵と換気不良が深刻で、じん肺による「よろけ」「気絶」と呼ばれる病が記録されているが、これらの記録は主に江戸時代のものである。「日本の銀が16世紀後半の世界産出量の3分の1を占めた」という説明が広く行われているが、算出の根拠は明確でなく、推計の幅が大きい点に注意が要る。',
    },
    year: [1526, 1600],
    keywords: ['石見銀山', '灰吹法', '間歩', '神屋寿禎', '生野銀山', '銀'],
    caveat:
      '「日本の銀が世界の産出量の3分の1を占めた」という説明はよく見かけますが、当時の世界の産銀量も日本の産銀量も正確な統計はなく、推計の幅が大きい数字です。鉱山労働の過酷さを伝える記録の多くは江戸時代のもので、戦国期の実態は史料が限られています。',
    gameNote:
      '盤上では「銭+4」という大きな収入にしていますが、鉱山の開発には技術者の招致と長い試掘が必要で、必ず当たるものではありませんでした。「兵-1」は坑道を掘る人手を表しています。',
  },
};

/** 湊と廻船。堺・博多の自治にも触れる。 */
export const MINATO_SEIBI: Card = {
  id: 'card:naisei:minato',
  name: '湊の整備',
  reading: 'みなとのせいび',
  type: 'naisei',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    // 問丸と廻船を呼び、津料を得る
    { t: 'gain', res: 'zeni', n: 3 },
    { t: 'draw', n: 1 },
  ],
  note: {
    summary:
      '船着き場・倉・市を整えて廻船を呼び込むこと。湊は年貢米や特産品の集散地であり、堺や博多のように商人が自ら町を運営した例もある。',
    detail: {
      1: '船がとまる場所や倉、市を整えて、遠くから船を呼びこむことです。湊には米や塩、布などが集まり、たくさんの銭が動きました。堺や博多では、商人たちが自分たちで町をおさめていました。',
      2: '湊の施設を整え、廻船や商人を呼び込む事業。中世の湊には問丸（といまる）と呼ばれる業者がいて、荷の保管・運送・取引の仲介を担い、のちの問屋へとつながった。大名は入港する船から津料（港湾使用料）を徴収し、重要な財源とした。堺は会合衆（えごうしゅう）と呼ばれる有力商人の合議によって、博多は年行司によって町の運営が行われ、堀や濠で町を囲んで自衛した。日明貿易や南蛮貿易の窓口となった湊は、物だけでなく情報や技術も運び込んだ。',
      3: '船着場・倉庫・市場・宿の整備によって廻船と商人を集める事業。中世の海運では問丸が荷の保管・輸送・売買仲介を一手に担い、湊ごとに問屋町を形成した。湊に入る船からは津料・入船料が徴収され、大名・寺社・公家がその徴収権をめぐって争っている。堺は36人の会合衆による合議制で運営され、環濠をめぐらせて傭兵を雇った。宣教師ガスパル・ヴィレラは永禄4年(1561)8月17日付の書簡（『耶蘇会士日本通信』所収）で、堺について「此の町はベニス市の如く執政官によりて治めらる」と記している。博多も年行司による町政が行われた。越前敦賀・若狭小浜は日本海と京を結ぶ結節点、伊勢大湊は伊勢神宮への物資と伊勢参りの人を運ぶ湊、越後直江津は青苧の積出港として機能した。湊には水主（かこ）・船大工・鍛冶・宿の女主人など多様な職の人々が集まり、遊女や宿を営む女性の記録も残る。ただし「東洋のベニス」という固定した呼び名自体は近代以降のもので、ヴィレラの記述も、堺に滞在した宣教師が見聞した限りでの理解である点に注意が要る。',
    },
    year: [1400, 1600],
    keywords: ['湊', '問丸', '津料', '堺', '会合衆', '廻船'],
    caveat:
      '宣教師ガスパル・ヴィレラは1561年の書簡で堺を「ベニス市の如く執政官によりて治めらる」と記しており、ベニスとの比較そのものは同時代の史料にあります。ただし「東洋のベニス」という固定した呼び名は近代以降のもので、ヴィレラの記述も外から見た理解です。堺の自治も完全な独立ではなく、幕府や有力大名との関係のなかで成り立っていました。',
    gameNote:
      '盤上では「銭+3・カードを1枚引く」としています。カードを引くのは、湊に物だけでなく情報や技術が入ってきたことを表した処理です。',
  },
};

/** 特産の育成。青苧を軸に、村の手仕事と女性の労働を書く。 */
export const AOSO_TOKUSAN: Card = {
  id: 'card:naisei:aoso',
  name: '特産の育成',
  reading: 'とくさんのいくせい',
  type: 'naisei',
  cost: { zeni: 1 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'zeni', n: 2 },
    { t: 'gain', res: 'koku', n: 1 },
  ],
  note: {
    summary:
      '国ごとの産物を育て、京や他国へ売って銭を得ること。越後の青苧（あおそ）、美濃の紙、瀬戸内の塩などが知られ、栽培や加工の多くを村の家々が担った。',
    detail: {
      1: '国ごとにとくべつな産物をそだてて、京やほかの国に売り、お金をえることです。越後（いまの新潟県）の青苧という布のもとになる草や、美濃（いまの岐阜県）の紙などが有名でした。糸をつむいだり布をおったりする仕事は、家の女の人たちが多くしていました。',
      2: '領国の産物を育成し、他国へ売って銭を得る政策。越後では青苧（からむしの繊維で、上質な麻布の原料）が主要な産物で、直江津から船で敦賀へ運ばれ、京や天王寺へ送られた。青苧の取引は青苧座が担い、上杉氏は座からの役銭を財源としていた。ほかに美濃紙、瀬戸内の塩、能登や若狭の海産物、大和の晒などが知られる。原料の栽培、糸を績む（うむ）、布を織るといった工程の多くは村の家で行われ、女性の労働が大きな比重を占めた。',
      3: '領国の特産品を保護・育成し、その流通から得られる役銭を財源とする政策。越後の青苧は苧麻（からむし）から採る靭皮繊維で、上布の原料として高い価値を持った。生産地の越後府中、中継地の近江坂本、消費地の京都・天王寺を結ぶ流通は青苧座が掌握し、座役の徴収権は公家の三条西家が持っていた。上杉氏（長尾氏）は蔵田氏に町人の統轄と青苧座の管理を委ね、直江津の船道前などから収入を得ている。青苧が謙信の軍事行動を支えたとする説明は広く行われるが、収入額を示す具体的な数字については後世の推計に依るところが大きい。ほかに美濃紙・杉原紙などの製紙、瀬戸内の入浜以前の塩田（揚浜）による製塩、若狭・能登の海産物、河内の木綿（16世紀に栽培が広がる）などが地域の特産として成長した。これらの工程のうち、苧を績む、糸を紡ぐ、布を織るといった作業は主として家内で女性が担っており、村の現金収入の重要な部分を女性の手仕事が支えていた。',
    },
    year: [1450, 1600],
    keywords: ['青苧', '青苧座', '越後', '美濃紙', '塩', '女性の労働'],
    caveat:
      '青苧の交易が上杉氏の財政をどれほど支えたかについて、同時代の帳簿が残っているわけではありません。よく引かれる収入額は後世の推計で、幅のある数字として扱う必要があります。',
    gameNote:
      '盤上では1枚で「銭+2・石高+1」としていますが、特産の育成は数十年単位で進むもので、1ターンで成果が出るものではありません。',
  },
};

// ---------------------------------------------------------------------------
// 道と関
// ---------------------------------------------------------------------------

/** 伝馬。盤上の進軍に効く札。合戦の役には触れない。 */
export const KAIDOU_DENMA: Card = {
  id: 'card:naisei:denma',
  name: '街道と伝馬',
  reading: 'かいどうとてんま',
  type: 'naisei',
  cost: { zeni: 2 },
  timing: 'anytime',
  effects: [
    // 道が整い、宿ごとに馬が用意される。進軍が速くなる
    { t: 'diceMod', n: 1 },
    { t: 'gain', res: 'zeni', n: 1 },
  ],
  note: {
    summary:
      '道を広げ、宿ごとに人と馬を用意させる制度。文書と物資が速く動くようになり、大名の命令が領国のすみずみまで届くようになった。',
    detail: {
      1: '道をひろげ、宿場ごとに人と馬を用意しておくしくみです。急ぎの手紙や荷物を、宿から宿へと馬をのりついで運びます。大名の命令が、国のすみずみまではやくとどくようになりました。',
      2: '街道を整備し、宿ごとに一定数の人足と馬を常備させて公用の通信・輸送にあてる制度。北条氏は虎の印判を押した伝馬手形を発行し、手形を持つ者だけが伝馬を使えるようにして濫用を防いだ。武田氏も領国内に伝馬の制度を敷いている。道の整備は軍勢の移動を速めるだけでなく、年貢米や商品の輸送費を下げ、市の発展にもつながった。宿には旅人を泊める家や馬を扱う人が集まり、宿場町が形づくられていった。',
      3: '道路の改修と、宿ごとに人馬を常備させる伝馬制からなる交通政策。戦国大名は軍勢の移動と文書伝達の速度を確保するため、街道の幅員を確保し、渡河点に船渡しや橋を整え、宿駅に伝馬役を課した。後北条氏は虎朱印を用いた伝馬手形の制度を整え、手形の記載に従って人馬を出させることで、無秩序な徴発を抑制している。武田氏の領国でも伝馬定書が発給された。伝馬役は宿の百姓にとって重い負担であり、その代償として諸役免除や地子免除が与えられることが多かった。街道整備は流通コストの低下を通じて市の発達を促し、宿場町の形成につながる。なお、全国的な街道網として整備された五街道と一里塚は江戸幕府によるもので、戦国期の整備は領国単位にとどまる。武田信玄が甲斐と信濃を結ぶ軍用道「棒道」を築いたという説明が広く知られるが、その名称と成立時期については後世の記録に基づく部分が大きく、近年は慎重な見方も出されている。',
    },
    year: [1500, 1600],
    keywords: ['伝馬', '街道', '宿場', '虎朱印', '後北条氏', '交通'],
    caveat:
      '五街道や一里塚の整備は江戸幕府によるもので、戦国時代のものではありません。武田信玄が築いたとされる軍用道「棒道」についても、名称や成立時期の裏付けには議論があります。',
    gameNote:
      '盤上では「出目+1」としています。合戦は出目ではなく手役で決まるため、この札は進軍にのみ効きます（docs/06）。',
  },
};

/** 関所を止める。寺社・公家の収入を奪う側面を落とさない。 */
export const SEKISHO_TEISHI: Card = {
  id: 'card:naisei:sekisho',
  name: '関所停止',
  reading: 'せきしょていし',
  type: 'naisei',
  cost: { gunryaku: 1 },
  timing: 'anytime',
  effects: [
    // 関銭がなくなり、人と物の動きが速くなる
    { t: 'diceMod', n: 1 },
    { t: 'gain', res: 'zeni', n: 2 },
    // 関を収入源としていた寺社・公家との関係は悪くなる
    { t: 'lose', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '街道や湊に設けられた関所をやめさせ、関銭の徴収を止めること。物の値が下がり流通が速くなる一方、関を収入源としていた寺社や公家は財源を失った。',
    detail: {
      1: '道や川わたしのところにつくられた関所をなくし、通るときのお金（関銭）をとらないようにすることです。荷物の値だんが下がり、人も物もはやく動くようになりました。ただし関所からお金をえていた寺や公家はこまりました。',
      2: '街道・渡し・湊などに設けられた関所を撤廃し、関銭の徴収を止める政策。中世の関所は幕府・寺社・公家・地元の領主などが設け、通行料を収入としていた。京都の七口をはじめ関の数は多く、そのたびに料金がかかるため物価が上がり、流通の妨げになっていた。織田信長は永禄11年（1568年）の上洛後、領国内の関所を撤廃したと『信長公記』に記されている。ただし関の撤廃は関を財源としていた勢力の反発を招き、また国境の要地には関が残された。',
      3: '関所の廃止と関銭徴収の停止を命じる政策。中世の関所は交通の要地に設けられ、幕府・朝廷・寺社・公家・在地領主が設置主体となって関銭を徴収した。京都七口の関は幕府や公家の財源であり、応仁の乱期には日野富子が関を設けて関銭を得たと伝えられる。関の乱立は輸送費を押し上げ、市場の統合を妨げた。織田信長は永禄11年(1568)の入京後、領国内の関所を撤廃したと『信長公記』は記し、その理由を「天下のため、往還の旅人のため」としている。もっとも、撤廃は信長の勢力圏に限られ、境目の要害には引き続き番所が置かれた。関の撤廃は寺社・公家の得分を直接奪う行為でもあり、比叡山延暦寺との関係悪化の一因になったとする見方もある。関所廃止を「自由な経済の実現」と一面的に評価することは難しく、誰の収入を誰に付け替える政策であったかを併せて見る必要がある。他の大名でも六角氏や今川氏に関の撤廃・規制の例があり、信長固有の政策ではない。',
    },
    year: [1568, 1590],
    keywords: ['関所', '関銭', '京都七口', '信長公記', '流通', '寺社'],
    caveat:
      '織田信長が全国の関所を廃止したわけではありません。撤廃は自らの勢力圏に限られ、境目には番所が置かれました。関所の撤廃例は信長以前にも他の大名に見られます。',
    gameNote:
      '盤上では「出目+1・銭+2・威信-1」としています。威信が下がるのは、関を財源としていた寺社や公家の反発を表したもので、関所の撤廃が悪い政策だったという評価ではありません。',
  },
};

// ---------------------------------------------------------------------------
// 国の仕組み
// ---------------------------------------------------------------------------

/** 分国法。喧嘩両成敗と、家臣が起草した六角氏式目に触れる。 */
export const BUNKOKUHOU: Card = {
  id: 'card:naisei:bunkokuhou',
  name: '分国法',
  reading: 'ぶんこくほう',
  type: 'naisei',
  cost: { zeni: 1, gunryaku: 1 },
  timing: 'council',
  // 最初期の分国法『今川仮名目録』は大永6年(1526)。第II章（1521-1559）以降に解禁する
  availableFrom: 2,
  effects: [
    // 家中の争いに裁定の基準ができ、当主の判断が通りやすくなる
    { t: 'gain', res: 'gunryaku', n: 2 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '戦国大名が自分の領国で定めた法。家臣どうしの争いの裁き方、所領の売買、年貢や訴訟の手続きなどを条文にした。喧嘩両成敗の規定で知られる。',
    detail: {
      1: '大名が自分の国のなかだけで通用するきまりを、文章にして定めたものです。けんかをしたら理由をとわず両方をばっする「喧嘩両成敗」などが書かれました。争いごとを大名が裁くことで、国のなかが落ちつきました。',
      2: '戦国大名が領国内に定めた法。今川氏親の『今川仮名目録』（1526年）、伊達稙宗の『塵芥集』（1536年）、武田晴信（信玄）の『甲州法度之次第』（1547年）などが知られる。内容は家臣間の紛争処理、所領の売買や質入れの制限、年貢や夫役の規定、訴訟の手続きなど広い範囲に及ぶ。喧嘩をした者は理非を問わず双方を処罰する喧嘩両成敗の規定は、家臣どうしの私的な武力行使を禁じ、争いの裁定を大名に集めるためのものだった。',
      3: '戦国大名が分国内で施行した法典。代表的なものに大永6年(1526)の『今川仮名目録』、天文5年(1536)の『塵芥集』（伊達稙宗、171箇条と分国法中最大）、天文16年(1547)の『甲州法度之次第』、永禄10年(1567)の『六角氏式目』、慶長2年(1597)の『長宗我部元親百箇条』などがある。規定内容は所領関係、刑事、訴訟手続、年貢・夫役、宗教統制など多岐にわたる。喧嘩両成敗法は自力救済（当事者による実力での紛争解決）を禁じ、裁定権を大名に一元化する意味を持ったが、実際には当事者間の和与（和解）が広く行われ、法が常に貫徹したわけではない。注目すべきは『六角氏式目』で、これは家臣団が起草し、当主の六角義賢・義治が承認する形をとっている。分国法を「大名の絶対的な権力の表れ」とのみ理解するのは適切でなく、家臣団との合意や、在地の慣習法を成文化した側面を含む。また今川仮名目録追加では、守護の権限が幕府の任命によるものではなく自らの実力によると明言する条文があり、室町的秩序からの離脱を示す例としてしばしば引かれる。',
    },
    year: [1526, 1597],
    keywords: ['分国法', '今川仮名目録', '塵芥集', '甲州法度之次第', '喧嘩両成敗', '六角氏式目'],
    caveat:
      '分国法は大名が一方的に押しつけた法とは限りません。『六角氏式目』は家臣団が起草して当主が承認したもので、在地の慣習を成文化した条文も多く含まれます。喧嘩両成敗も、実際には当事者間の和解が広く行われていました。',
    gameNote:
      '盤上では「軍略+2・威信+1」としています。軍略が増えるのは、家中の争いを裁く基準ができて当主の判断が通りやすくなることを表した処理です。この札は最初期の分国法『今川仮名目録』（1526年）に合わせて第II章以降にしか出てきません。',
  },
};

/** 城下集住。兵農分離を戦国期に完成とみない書き方にする。 */
export const JOKAMACHI: Card = {
  id: 'card:naisei:jokamachi',
  name: '城下町',
  reading: 'じょうかまち',
  type: 'naisei',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [
    // 家臣と商工業者を城の周りに集める
    { t: 'gain', res: 'hei', n: 2 },
    { t: 'gain', res: 'zeni', n: 2 },
    // 村を離れる者が出て、在地の生産は一時的に細る
    { t: 'lose', res: 'koku', n: 1 },
  ],
  note: {
    summary:
      '城の周りに家臣・商人・職人を集めて住まわせた町。一乗谷や小田原、山口などが知られ、発掘によって当時の町割りや暮らしぶりが明らかになっている。',
    detail: {
      1: '城のまわりに、家来や商人・職人を集めて住まわせた町です。朝倉氏の一乗谷（いまの福井県）では、地面をほって調べた結果、道や家のならび、使っていた道具までわかってきました。',
      2: '戦国大名が城の周辺に家臣や商工業者を集住させて形成した町。越前の一乗谷（朝倉氏）、相模の小田原（後北条氏）、周防の山口（大内氏）、豊後の府内（大友氏）などが知られる。大名は町に地子（宅地税）の免除や市の特権を与えて人を集め、道と町割りを定めた。一乗谷では発掘調査によって武家屋敷・町屋・寺院・庭園の配置が明らかになり、櫛や漆器、輸入陶磁器など生活の道具も多数出土している。町には商人・職人の家族が暮らし、女性が商いや機織りに従事した痕跡も残る。',
      3: '大名の居城を核として、家臣屋敷・町人地・寺社を計画的に配置した都市。越前一乗谷、相模小田原、周防山口、豊後府内、甲斐甲府などが代表例で、一乗谷は谷を城戸で仕切った線状の町割り、小田原は総構による大規模な囲い込みという異なる形態をとる。大名は地子免除・楽市・伝馬の便などを与えて商工業者を誘致し、職人を町ごとに編成した（鍛冶町・紺屋町などの地名がその名残）。一乗谷朝倉氏遺跡の発掘では、武家屋敷と町屋の区画、上水・排水の施設、庭園、越前焼や中国製青磁、櫛・かんざしなどの生活用具が大量に出土し、文献では分からない都市生活の実態が明らかになった。城下集住は家臣を在地から切り離す意味を持ち、しばしば兵農分離の進展として語られるが、戦国期における集住は不徹底で、多くの家臣が在地に基盤を残していた。兵農分離を段階的・地域的な過程としてとらえる見方が、近年の研究では一般的である。',
    },
    year: [1470, 1600],
    keywords: ['城下町', '一乗谷', '小田原', '山口', '地子免除', '城下集住'],
    caveat:
      '城下町への集住によって戦国期に兵農分離が完成したという説明は、近年の研究では見直されています。多くの家臣は在地に基盤を残しており、分離は地域差の大きい段階的な過程でした。',
    gameNote:
      '盤上では「兵+2・銭+2・石高-1」としています。石高が下がるのは在地から人が移ることを表した処理で、実際に城下町の造成が領国の石高を減らしたわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 村と町の力
// ---------------------------------------------------------------------------

/** 惣掟。民衆の側の秩序を、守る側の役の補正として盤上に出す。 */
export const SOU_OKITE: Card = {
  id: 'card:naisei:soukite',
  name: '惣掟',
  reading: 'そうおきて',
  type: 'naisei',
  cost: { gunryaku: 1 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'koku', n: 1 },
    { t: 'gain', res: 'ishin', n: 1 },
    // 村が自ら秩序を保ち、外からの侵入に備える。守る側の役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'defend' },
  ],
  note: {
    summary:
      '村（惣村）が寄合で定めた自分たちの掟。入会地の使い方、用水の分配、村を荒らす者への処罰などを決め、村自身が裁く自検断も行われた。',
    detail: {
      1: '村の人たちが集まって話し合い（寄合）、自分たちできめたきまりです。山や野原の使い方、水の分け方、悪いことをした人のばっし方などが書かれました。村は自分たちで見はりをおき、身を守ることもありました。',
      2: '中世後期の村（惣村）が寄合で定めた規約。近江の今堀日吉神社に伝わる掟や、菅浦の文書などが有名で、入会地である山林原野の利用、用水の分配、盗みや放火への罰、村を出た者や余所者の扱いなどが定められている。村は乙名（おとな）や年寄と呼ばれる代表を選び、年貢を村がまとめて納める村請も行われた。掟破りを村自身が裁く自検断（地下検断）も広く見られる。戦乱の際には村ごと山に逃げたり、堀や柵で村を囲んで守ったりした。',
      3: '惣村が寄合の決議によって定めた自治規約。近江蒲生郡の今堀日吉神社文書に残る掟や、琵琶湖北岸の菅浦の惣庄文書が代表的な史料で、山野の入会利用、用水の配分と管理、祭祀の負担、盗犯・放火への制裁、他所者の居住制限などが条文化されている。村政は乙名・年寄・番頭などの役職者が担い、年貢を村単位で請け負う村請の体制がとられた地域では、村が領主に対する交渉主体としても機能した。掟違反者への処分を村自身が行う自検断は、領主の裁判権と競合しつつ広く行われている。惣村は宮座を通じて結束し、戦乱時には村を堀や土塁で囲み、武具を備えて自衛した。菅浦と大浦の間で続いた日指・諸河の相論のように、村と村が長期にわたって争い、幕府の裁許を仰いだ事例も残る。惣村の担い手は男性の家長が中心だったが、農作業・手仕事・年貢負担の実態においては女性の労働が不可欠であり、村落史を家長の記録だけで読むことはできない。',
    },
    year: [1350, 1600],
    keywords: ['惣村', '惣掟', '寄合', '自検断', '今堀', '菅浦'],
    caveat:
      '惣村の自治を近代の自治や民主主義になぞらえる説明は、当時の実態とずれます。寄合に加わるのは村の有力な家の家長が中心で、村の内部にも身分や格差がありました。',
    gameNote:
      '盤上では「守る側の役が1段階上がる」としています。これは村が堀や柵で自衛した事実をルールに翻訳したもので、惣掟という文書そのものが防御力を持ったという意味ではありません。',
  },
};

/** 寺内町。宗教勢力は事実の記述に留める（docs/05 §4-3）。 */
export const JINAIMACHI: Card = {
  id: 'card:naisei:jinaimachi',
  name: '寺内町',
  reading: 'じないまち',
  type: 'naisei',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    {
      t: 'choice',
      options: [
        {
          // 諸役免除と守護不入を認める。年貢は減るが、市が立ち銭が入る
          label: '諸役を免じ、寺内の自治を認める',
          then: [
            { t: 'lose', res: 'koku', n: 1 },
            { t: 'gain', res: 'zeni', n: 3 },
          ],
        },
        {
          // 通常の村と同じに扱う。収入は増えるが、住人は離れる
          label: '他の村と同じに諸役を課す',
          then: [
            { t: 'gain', res: 'koku', n: 2 },
            { t: 'lose', res: 'ishin', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '浄土真宗（一向宗）の寺院を中心に、土塁と濠で囲んで形成された町。諸役の免除や守護不入の特権を得て、商工業の拠点となった例がある。',
    detail: {
      1: 'お寺を中心にして、まわりを土のかべやほりでかこんだ町です。年貢や役をまぬかれる特権をもらった町もあり、商人や職人が集まってにぎわいました。富田林や今井（いまの大阪府・奈良県）などが知られています。',
      2: '浄土真宗（一向宗）の寺院や道場を核として形成された町。土居（土塁）と濠で囲まれ、町のなかは道が碁盤の目状に区切られた。領主から諸役の免除や守護不入（領主の役人が立ち入らない特権）を認められた町もあり、商人や職人が集まって商工業の拠点となった。摂津の石山、河内の富田林、大和の今井、和泉の貝塚などが知られる。今井は「大和の金は今井に七分」と言われるほど商業が栄え、江戸時代には両替商も置かれた。',
      3: '真宗寺院・道場を中核として、土居と濠で囲繞された集落・都市。16世紀に畿内や北陸で展開し、摂津石山、河内富田林、大和今井、和泉貝塚、越前吉崎などが知られる。寺内町は領主から諸役免除・守護不入・徳政不入などの特権を認められる場合があり、これらは寺院と領主の交渉によって成立した個別の権利であって、宗教的な理由で一律に与えられたものではない。町の運営は年寄・惣中による合議で行われ、環濠と門によって出入りを管理した。今井（現在の橿原市今井町）は東西約600メートル、南北約310メートルの範囲に江戸期の町家が多数現存し、重要伝統的建造物群保存地区に指定されている。寺内町は物資の集散地・金融の拠点として機能する一方、織田信長との石山合戦のように、政治権力との対立の場ともなった。一向一揆や寺内町を「宗教勢力の武装」として単純に括ることはできず、門徒の組織、在地の国人、商工業者の利害が重なり合った複合的な存在として理解する必要がある。',
    },
    year: [1470, 1600],
    keywords: ['寺内町', '守護不入', '今井', '富田林', '環濠', '真宗'],
    caveat:
      '寺内町の特権は宗教的な理由で一律に与えられたものではなく、寺院と領主の個別の交渉で成立したものです。現在の今井町に残る町並みの多くは江戸時代の建物で、戦国期の姿がそのまま残っているわけではありません。',
    gameNote:
      '盤上では「自治を認める／諸役を課す」の二択にしていますが、これは当時の領主が置かれた立場を選択肢に整理したものです。どちらかが正しい選び方だったという評価を含むものではありません。',
  },
};

// ---------------------------------------------------------------------------
// 合戦に効く内政
// ---------------------------------------------------------------------------

/** 石垣普請。城そのものは docs/06 §4 の段階補正で効く。 */
export const ISHIGAKI_BUSHIN: Card = {
  id: 'card:naisei:ishigaki',
  name: '石垣普請',
  reading: 'いしがきぶしん',
  type: 'naisei',
  cost: { zeni: 3 },
  timing: 'council',
  // 総石垣の城が現れるのは16世紀後半。安土城の起工は天正4年(1576)＝第III章（1560-1581）
  availableFrom: 3,
  effects: [
    // 石を運び、積む人手が要る
    { t: 'lose', res: 'hei', n: 2 },
    { t: 'build', level: 'kyojou' },
  ],
  note: {
    summary:
      '自然石をそのまま積む野面積みなどの技法で、城に石垣を築くこと。土の城から石垣と礎石建物を持つ城へという変化は、16世紀後半に畿内から広がった。',
    detail: {
      1: '城のまわりに石をつみあげて、くずれにくい壁をつくる工事です。切らないままの石をくみ合わせる「野面積み」というやりかたがありました。それまでの城は土をけずってつくるものが多く、石垣をもつ城は新しいものでした。',
      2: '城郭に石垣を築く工事。中世の城の多くは山を削り土を盛った土の城だったが、16世紀後半になると畿内を中心に、石垣・礎石建物・瓦を備えた城が現れる。天正4年（1576年）に築かれ始めた安土城はその代表とされる。自然石を加工せずに積む野面積みは、隙間から水が抜けるため崩れにくいという利点があった。石垣普請には石を切り出し、運び、積む多くの人手が必要で、村から人足が徴発された。',
      3: '城郭に石積みの構造物を築く土木工事。中世城郭は削平と土塁による土の城が主流で、部分的な石積みは存在したものの、高石垣で全体を囲む総石垣の城が現れるのは16世紀後半である。天正4年(1576)から築かれた安土城は、高石垣・礎石建物・瓦・天主を備えた画期的な城として位置づけられてきた。石垣の技法には、自然石をほぼ未加工で積む野面積み、接合面を加工する打込接、方形に整形する切込接があり、時代とともに加工度が上がる。石工集団としては比叡山延暦寺の山麓、近江坂本の穴太に居住した穴太衆が有名で、江戸期には諸藩に召し抱えられた。ただし安土城の石垣を穴太衆が積んだとする通説には、それを裏付ける同時代史料がなく、発掘成果からも特定の集団の関与を示せないとして、中井均らが疑問を呈している。石垣普請には石材の採取・運搬・据付に膨大な労働力が必要で、大名は領内の村に普請役として人足を課した。城は防御施設であると同時に、石垣と天主の高さによって権力を可視化する装置でもあった。',
    },
    year: [1570, 1600],
    keywords: ['石垣', '野面積み', '穴太衆', '安土城', '普請役', '城郭'],
    caveat:
      '安土城の石垣を穴太衆が積んだという説明は広く知られていますが、それを裏付ける同時代の史料はなく、近年は疑問が示されています。穴太衆の活躍が確実にたどれるのは主に江戸時代以降です。',
    gameNote:
      '盤上では1枚で居城が建ちますが、実際の石垣普請は年単位の工事です。この札は総石垣の城が現れる時期（安土城の起工は1576年）に合わせて第III章以降にしか出てきません。城が「守る側の役を1段階上げる」効果は docs/06 §4 の段階補正によるもので、この札そのものの効果ではありません。',
  },
};

/** 兵糧。手勢札を1枚増やす形で「兵を出し続けられる」を表す。 */
export const HYOUROU_NO_SONAE: Card = {
  id: 'card:naisei:hyourou',
  name: '兵糧の備え',
  reading: 'ひょうろうのそなえ',
  type: 'naisei',
  cost: { zeni: 2 },
  timing: 'battle',
  effects: [
    // 兵糧が続く限り、より多くの兵を戦場に置いておける。手勢札が1枚増える
    { t: 'holeCardMod', n: 1 },
  ],
  note: {
    summary:
      '城や陣に米・塩・味噌などを蓄えること。兵糧が尽きるかどうかが戦の長さを決め、兵糧の徴発は村にとって重い負担となった。',
    detail: {
      1: '城や陣に、米や塩、味噌をためておくことです。食べ物がなくなれば戦をつづけられないので、どれだけたくわえがあるかがとても大事でした。兵糧は村から集められたので、村の人たちの負担になりました。',
      2: '軍勢を維持するための食糧の備蓄。城には兵糧蔵が設けられ、米・塩・味噌・干魚などが蓄えられた。兵糧は領内の村から徴発されるほか、商人から銭で買い付けられることもあり、遠征では兵糧を運ぶ小荷駄隊が編成された。攻める側が城の周囲を囲んで補給を断つ兵糧攻めは、双方に多くの犠牲を出す方法で、城内の人々や周辺の村人が飢えに苦しんだ記録が残る。兵糧の確保は、戦を始められるかどうか、どれだけ続けられるかを直接左右した。',
      3: '軍事行動を支える食糧・秣（まぐさ）の確保と輸送。戦国期の軍勢は当初、兵士が自弁で数日分の兵糧を携行する形が基本だったが、遠征の長期化に伴い、大名が兵糧を集積して支給する体制が整えられていく。城には兵糧蔵が置かれ、籠城に備えて数か月分が備蓄された。補給は小荷駄隊が担い、街道と伝馬、湊と廻船の整備は兵糧輸送の前提でもあった。兵糧の調達は村からの徴発（兵粮米）と商人からの買い付けの双方によったが、いずれも在地の負担となり、戦時の村では兵糧の徴発と乱取り（軍勢による人や物の略奪）が生活を直撃した。城を包囲して補給路を断つ戦法は、天正6年(1578)からの三木城、天正9年(1581)の鳥取城の例が知られ、城内で多数の餓死者が出たことが記録されている。こうした戦法を戦術上の巧拙として語ることはできるが、そこで飢えたのは兵だけでなく、城に逃げ込んだ周辺の百姓や女性・子どもを含む人々であった。',
    },
    year: [1500, 1600],
    keywords: ['兵糧', '小荷駄', '兵糧蔵', '籠城', '兵粮米', '補給'],
    caveat:
      '兵糧攻めで城内に多数の餓死者が出たことは複数の記録に残りますが、具体的な人数は史料によって差が大きく、確かな数字はわかっていません。',
    gameNote:
      '盤上では「手勢札を1枚増やす」としています。兵糧が続けば多くの兵を戦場に置いておける、という関係を docs/06 §4 の手勢札の枚数に置き換えた処理です。',
  },
};

/** 鉄砲鍛冶。鉄砲が関わるので第II章以降にしか出ない。 */
export const TEPPOU_KAJI: Card = {
  id: 'card:naisei:teppoukaji',
  name: '鉄砲鍛冶の招致',
  reading: 'てっぽうかじのしょうち',
  type: 'naisei',
  cost: { zeni: 3 },
  timing: 'battle',
  availableFrom: 2,
  effects: [
    // 武具の質が揃い、攻守どちらでも役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'both' },
  ],
  note: {
    summary:
      '鉄砲を作る鍛冶職人を城下に招くこと。近江の国友、和泉の堺、紀伊の根来が生産地として知られ、鉄砲の国産化は伝来から短期間で進んだ。',
    detail: {
      1: '鉄砲をつくる鍛冶（かじ）の職人を、自分の町によぶことです。近江の国友（いまの滋賀県）、和泉の堺（いまの大阪府）、紀伊の根来（いまの和歌山県）が有名なつくり場所でした。日本でも鉄砲がつくられるようになりました。',
      2: '鉄砲の製造を担う鍛冶職人を招き、領内で鉄砲を生産できるようにすること。天文12年（1543年）に種子島へ伝わった鉄砲は、その後短期間で各地に広まり、近江の国友、和泉の堺、紀伊の根来が主要な生産地となった。『國友鉄砲記』は、天文13年（1544年）に将軍足利義晴が国友の鍛冶に鉄砲の製作を命じたと伝えるが、この書物は伝来から90年後の1633年に編まれたもので、記述の信頼性には疑問が示されている。鉄砲づくりには鉄の加工技術に加え、ねじの製作や火薬の調達が必要だった。',
      3: '火縄銃の生産体制を領内に確保するための職人招致。天文12年(1543)の種子島への伝来後、国内での製造は急速に広がり、近江国友、和泉堺、紀伊根来が代表的な生産地となった。国友では『國友鉄砲記』が天文13年(1544)に将軍足利義晴の命で国友善兵衛らが製作にあたったと伝えるが、同書は寛永10年(1633)の編纂で、発祥に関する記述には慎重な検討が必要とされている。鉄砲の製造には、銃身を鍛接する技術、尾栓の雄ねじ・雌ねじを切る技術、からくり（機関部）の製作が必要で、いずれも既存の刀鍛冶・鋳物師の技術基盤の上に成立した。火薬の原料のうち硫黄と木炭は国内で得られたが、硝石は輸入に依存し、のちに古土法（床下の土から硝石を採る方法）による国産化が試みられている。鉄砲の普及は合戦の形を変えたが、変化は連続的で、鉄砲が一夜にして戦術を刷新したわけではない。なお長篠の戦い(1575)の「三段撃ち」は、同時代の一次史料に裏付けを欠き、後世の軍記による記述と考えられている。',
    },
    year: [1543, 1600],
    keywords: ['鉄砲鍛冶', '国友', '堺', '根来', '火縄銃', '硝石'],
    caveat:
      '『國友鉄砲記』が伝える「1544年に将軍の命で国産化した」という話は、1633年に編まれた書物によるもので、そのまま史実とはできません。長篠の戦いの「三段撃ち」も一次史料による裏付けが乏しく、後世の軍記による記述と考えられています。',
    gameNote:
      '盤上では「攻守どちらでも役が1段階上がる」としており、第II章（鉄砲伝来）以降にしか使えません。鉄砲そのものの強さは docs/06 §4 の段階補正で別に扱っており、この札は生産体制を持つことの利を表しています。',
  },
};

// ---------------------------------------------------------------------------

/** 内政カード20枚。宿場・湊で購入し、評定で引く。 */
export const NAISEI_CARDS: readonly Card[] = [
  RAKUICHI_RAKUZA,
  ROKUSAI_ICHI,
  ERIZENI_REI,
  TOKUSEI_REI,
  KENCHI,
  TSUTSUMI_BUSHIN,
  YOUSUI,
  SHINDEN_KAIHATSU,
  KOUZAN_KAIHATSU,
  MINATO_SEIBI,
  AOSO_TOKUSAN,
  KAIDOU_DENMA,
  SEKISHO_TEISHI,
  BUNKOKUHOU,
  JOKAMACHI,
  SOU_OKITE,
  JINAIMACHI,
  ISHIGAKI_BUSHIN,
  HYOUROU_NO_SONAE,
  TEPPOU_KAJI,
];
